import type { App } from 'vue'
import * as Sentry from '@sentry/vue'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { AschehougOidcClient, RequestScopes } from '@aschehoug/aschehoug-oidc-client'

import { i18n } from '../translations'
import { pinia } from '@/main'
import { router } from '@/router'

import CodeBlock from '@/components/global/CodeBlock.vue'
import ColorBox from '@/components/global/ColorBox.vue'
import ExpandElement from '@/components/global/ExpandElement.vue'
import FileDownload from '@/components/common/FileDownload.vue'
import FileDownloadBlock from '@/components/global/FileDownloadBlock.vue'
import FileDownloadInline from '@/components/global/FileDownloadInline.vue'
import Glossary from '@/components/global/Glossary.vue'
import GlossCollection from '@/components/global/GlossCollection.vue'
import HighlightedBox from '@/components/global/HighlightedBox.vue'
import MathFormula from '@/components/global/MathFormula.vue'
import ReadMore from '@/components/global/ReadMore.vue'
import Sentence from '@/components/global/Sentence.vue'
import SourceInline from '@/components/global/SourceInline.vue'
import TermExplanationButton from '@/components/global/TermExplanationButton.vue'
import { ksSetup, KsToast, KsTooltip } from '@aschehoug/kloss'
import AudioPlayer from '@/components/global/AudioPlayer.vue'
import VideoPlayer from './components/global/VideoPlayer.vue'
import ExternalLink from '@/components/global/ExternalLink.vue'
import GeoGebraApplet from '@/components/global/GeoGebraApplet.vue'
import PronunciationBoxCollection from '@/components/global/PronunciationBoxCollection.vue'

type Setup = (app: App<Element>) => void

const setPlugins: Setup = (app) => {
  app.use(VueQueryPlugin)
  app.use(pinia)
  app.use(router)
  app.use(i18n)
}

const setGlobalComponents: Setup = (app) => {
  app.component('AudioPlayer', AudioPlayer)
  app.component('CodeBlock', CodeBlock)
  app.component('ColorBox', ColorBox)
  app.component('ExpandElement', ExpandElement)
  app.component('ExternalLink', ExternalLink)
  app.component('FileDownload', FileDownload)
  app.component('FileDownloadBlock', FileDownloadBlock)
  app.component('FileDownloadInline', FileDownloadInline)
  app.component('GeoGebraApplet', GeoGebraApplet)
  app.component('Glossary', Glossary)
  app.component('GlossCollection', GlossCollection)
  app.component('HighlightedBox', HighlightedBox)
  app.component('MathFormula', MathFormula)
  app.component('PronunciationBoxCollection', PronunciationBoxCollection)
  app.component('ReadMore', ReadMore)
  app.component('Sentence', Sentence)
  app.component('SourceInline', SourceInline)
  app.component('TermExplanationButton', TermExplanationButton)
  app.component('VideoPlayer', VideoPlayer)
}

const setKloss: Setup = (app) => {
  app.use(KsToast, { duration: 3000 })
  app.directive('ks-tooltip', KsTooltip)
  ksSetup()
}


const setSentry: Setup = (app) => {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: import.meta.env.VITE_GIT_SHA,
    environment: import.meta.env.VITE_ENV,
    tracesSampleRate: import.meta.env.VITE_ENV === 'prod' ? 0.5 : 1.0,
    logErrors: true,
    integrations: [
      Sentry.browserTracingIntegration({ router })
    ],
    // set this to true if you need to track errors in development
    enabled: ['test', 'stage', 'prod'].includes(import.meta.env.MODE)
  })

}

export const createOidcClient = () => new AschehougOidcClient({
  authority: import.meta.env.VITE_AM_CORE,
  client_id: import.meta.env.VITE_AM_CLIENT_ID,
  redirect_uri: import.meta.env.VITE_HOST,
  redirect_feide_uri: import.meta.env.VITE_HOST + '/feide',
  scope: RequestScopes.openIdProfile,
  post_logout_redirect_uri: import.meta.env.VITE_HOST,
  response_type: 'code',
  metadata: {
    authorization_endpoint: import.meta.env.VITE_AM_CORE + '/am/oauth2/realms/root/authorize'
  },
  base_uri_am: import.meta.env.VITE_AM_CORE,
  selfService_api_url: import.meta.env.VITE_API_CORE,
  idCloudUrl: import.meta.env.VITE_ID_CLOUD,
  external_api_url: import.meta.env.VITE_CORE_EXTERNAL_API_URL,
  tokenCookieName: import.meta.env.VITE_OIDC_COOKIE_NAME,
  mediaTokenCookieName: import.meta.env.VITE_OIDC_MEDIA_COOKIE_NAME,
  cookieDomain: import.meta.env.VITE_OIDC_COOKIE_DOMAIN,
})

export const setup: Setup = (app) => {
  setPlugins(app)
  setSentry(app)
  setGlobalComponents(app)
  setKloss(app)
  app.mount('#app')
}
